class BlogRowHeights {
  constructor() {
    // Try to find blog container from either main blog page or blog-posts section
    this.blogContainer = document.querySelector('.blog .blog__articles') || 
                         document.querySelector('.blog-posts .content__feature');
    if (!this.blogContainer) return;

           // Virtual height constants (matching dynamic-line-clamp.js)
           this.virtualHeights = {
             imageHeight: {
               mobile: 450,
               tablet: 600,
               desktop: 700
             },
      categoryHeight: 20,
      titleHeight: 60,
      publishInfoHeight: 20,
      buttonHeight: 40,
      spacing125: 10,          // --spacing-125 (category/title/publish margin-bottom)
      spacing200: 16,          // --spacing-200 (excerpt margin-top)
      spacing400: 32,          // --spacing-400 (image margin-bottom)
      lineHeight: 24
    };

    this.init();
    window.addEventListener('resize', this.debounce(this.init.bind(this), 250));
  }

  init() {
    this.calculateRowHeights();
  }

  getViewportSize() {
    const width = window.innerWidth;
    if (width < 720) return 'mobile';
    if (width < 1024) return 'tablet';
    return 'desktop';
  }

  isSingleColumnLayout() {
    // Check if we're in single column layout (mobile or when CSS grid is set to 1 column)
    const width = window.innerWidth;
    const computedStyle = window.getComputedStyle(this.blogContainer);
    const gridColumns = computedStyle.gridTemplateColumns;
    
    // Single column if mobile width or if CSS grid is set to 1 column
    return width < 720 || gridColumns === '1fr' || gridColumns.includes('repeat(1,');
  }

  calculateRowHeights() {
    // Get all blog article items except featured ones
    // Handle both main blog page and blog-posts section structures
    let allItems = [];
    
    // For main blog page
    if (this.blogContainer.classList.contains('blog__articles')) {
      allItems = Array.from(this.blogContainer.querySelectorAll('.blog-article-item:not(.blog-article-item--featured)'));
    }
    // For blog-posts section
    else if (this.blogContainer.classList.contains('content__feature')) {
      allItems = Array.from(this.blogContainer.querySelectorAll('.blog-posts-featured-item'));
    }
    
    // Debug: Log found items
    console.log('BlogRowHeights: Found', allItems.length, 'items in', this.blogContainer.className);
    
    // Check if we're in single column layout
    const isSingleColumn = this.isSingleColumnLayout();
    
    if (isSingleColumn) {
      // For single column, let items use their natural content height
      allItems.forEach(item => {
        if (this.blogContainer.classList.contains('blog__articles')) {
          item.style.height = 'auto';
        } else if (this.blogContainer.classList.contains('content__feature')) {
          const metaContainer = item.querySelector('.blog-posts-meta');
          if (metaContainer) {
            metaContainer.style.height = 'auto';
          }
        }
      });
      
      return;
    }
    
    // Group items by their row position for multi-column layouts
    const rows = this.groupItemsByRow(allItems);
    
    // Calculate and apply heights for each row using virtual calculations
    rows.forEach((rowItems, rowIndex) => {
      this.setRowHeightVirtual(rowItems, rowIndex);
    });
  }

  groupItemsByRow(items) {
    const rows = [];
    const itemPositions = new Map();
    
    // Get the position of each item
    items.forEach(item => {
      const rect = item.getBoundingClientRect();
      const containerRect = this.blogContainer.getBoundingClientRect();
      
      // Calculate relative position within the container
      const relativeTop = rect.top - containerRect.top;
      itemPositions.set(item, relativeTop);
    });

    // Group items by their top position (with some tolerance for sub-pixel differences)
    const tolerance = 10; // pixels
    const groupedRows = [];
    
    items.forEach(item => {
      const itemTop = itemPositions.get(item);
      
      // Find existing row with similar top position
      let foundRow = false;
      for (let i = 0; i < groupedRows.length; i++) {
        const rowTop = groupedRows[i][0].getBoundingClientRect().top - this.blogContainer.getBoundingClientRect().top;
        if (Math.abs(itemTop - rowTop) <= tolerance) {
          groupedRows[i].push(item);
          foundRow = true;
          break;
        }
      }
      
      // If no matching row found, create a new one
      if (!foundRow) {
        groupedRows.push([item]);
      }
    });

    return groupedRows;
  }

  setRowHeightVirtual(rowItems, rowIndex) {
    const viewport = this.getViewportSize();
    const heights = this.virtualHeights;
    
    // Calculate virtual height for a representative blog post
    let virtualHeight;
    
    // For main blog page, include image height
    if (this.blogContainer.classList.contains('blog__articles')) {
      virtualHeight = heights.imageHeight[viewport]; // Image height
    } else {
      // For blog-posts section, find the tallest container in this row
      let maxHeight = 0;
      
      rowItems.forEach(item => {
        const metaContainer = item.querySelector('.blog-posts-meta');
        if (metaContainer) {
          // Get the natural height of this container
          const tempHeight = metaContainer.style.height;
          metaContainer.style.height = 'auto';
          const naturalHeight = metaContainer.offsetHeight;
          metaContainer.style.height = tempHeight;
          
          // Track the tallest container
          maxHeight = Math.max(maxHeight, naturalHeight);
        }
      });
      
      // Use the tallest height found, with a minimum for proper alignment
      virtualHeight = Math.max(maxHeight, 200);
    }
    
    // For main blog page, add content height calculations
    if (this.blogContainer.classList.contains('blog__articles')) {
      const category = rowItems[0].querySelector('.blog-posts-meta__category');
      const publishInfo = rowItems[0].querySelector('.blog-posts-meta__publish-info');
      
      if (category) {
        virtualHeight += heights.categoryHeight + heights.spacing125; // Category + margin-bottom
      }
      
      virtualHeight += heights.titleHeight + heights.spacing125; // Title + margin-bottom
      virtualHeight += heights.spacing200; // Excerpt margin-top
      
      if (publishInfo) {
        virtualHeight += heights.publishInfoHeight + heights.spacing125; // Publish info + margin-bottom
      }
      
      virtualHeight += heights.buttonHeight + 25; // Button height (margin-top: auto handles spacing)
    }
    
    // Apply the virtual height to all items in this row
    rowItems.forEach(item => {
      // For main blog page, set height on the item itself
      if (this.blogContainer.classList.contains('blog__articles')) {
        item.style.height = `${virtualHeight}px`;
      }
      // For blog-posts section, set height on the blog-posts-meta container
      else if (this.blogContainer.classList.contains('content__feature')) {
        const metaContainer = item.querySelector('.blog-posts-meta');
        if (metaContainer) {
          metaContainer.style.height = `${virtualHeight}px`;
        }
      }
    });
  }

  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new BlogRowHeights();
});
